/* eslint-disable react-hooks/static-components */
import React from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import { Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {
  getTemplate,
  getUiOptions,
  ArrayFieldTemplateProps,
  ArrayFieldTemplateItemType,
  UIOptionsType,
  UiSchema,
} from '@rjsf/utils';
import { iconButtonPillStyle } from 'components/button/iconButtonPillStyle';
import { ArrayContainer } from './ArrayFieldTemplate.styles';

type ArrayType = string[] | number[] | boolean[] | object[];

type UiOptions = UIOptionsType & {
  requiredItems?: ArrayType;
};

export default function ArrayFieldTemplate(props: ArrayFieldTemplateProps<ArrayType>) {
  const {
    canAdd,
    disabled,
    idSchema,
    uiSchema,
    items,
    onAddClick,
    readonly,
    registry,
    required,
    schema,
    title,
    formData,
  } = props;

  const uiOptions = getUiOptions(uiSchema) as UiOptions;
  const requiredIndexes = uiOptions.requiredItems
    ?.map((item) => formData?.findIndex((value) => value === item))
    .filter((index?: number) => index !== undefined && index !== -1);

  const ArrayFieldDescriptionTemplate = getTemplate('ArrayFieldDescriptionTemplate', registry, uiOptions);
  const ArrayFieldItemTemplate = getTemplate('ArrayFieldItemTemplate', registry, uiOptions);
  const ArrayFieldTitleTemplate = getTemplate('ArrayFieldTitleTemplate', registry, uiOptions);
  return (
    <>
      <ArrayContainer>
        <ArrayFieldTitleTemplate
          idSchema={idSchema}
          title={uiOptions.title ?? title}
          schema={schema}
          uiSchema={uiSchema as UiSchema}
          required={required}
          registry={registry}
        />
        {items?.map(({ key, ...itemProps }: ArrayFieldTemplateItemType, index: number) => {
          const isRequired = requiredIndexes?.includes(index);

          const childrenElement = itemProps.children as React.ReactElement<ArrayFieldTemplateItemType>;
          const children =
            isRequired !== childrenElement.props.disabled || isRequired !== childrenElement.props.readonly
              ? React.cloneElement(childrenElement, {
                  ...childrenElement.props,
                  disabled: isRequired ?? childrenElement.props.disabled,
                  readonly: isRequired ?? childrenElement.props.readonly,
                })
              : childrenElement;

          return (
            <ArrayFieldItemTemplate
              key={key}
              {...itemProps}
              disabled={isRequired ?? itemProps.disabled}
              readonly={isRequired ?? itemProps.readonly}
            >
              {children}
            </ArrayFieldItemTemplate>
          );
        })}
        {canAdd && (
          <Grid container justifyContent="flex-end">
            <Grid item={true}>
              <Box mt={1.5}>
                <Tooltip title="Add item">
                  <span style={{ display: 'inline-flex' }}>
                    <IconButton
                      aria-label="add item"
                      size="small"
                      sx={iconButtonPillStyle}
                      disabled={!!disabled || !!readonly}
                      onClick={onAddClick}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
        )}
      </ArrayContainer>
      <ArrayFieldDescriptionTemplate
        idSchema={idSchema}
        description={uiOptions.description ?? schema.description}
        schema={schema}
        uiSchema={uiSchema as UiSchema}
        registry={registry}
      />
    </>
  );
}
